import React, { useState, useEffect } from 'react';
import { Helmet } from "react-helmet";
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronDown, 
  faPaperPlane, 
  faRocket, 
  faLightbulb, 
  faUsers,
  faChartLine,
  faShieldAlt,
  faStar,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { api } from '../../../api';
import HeadComponent from '../../../head';
import Swal from 'sweetalert2';

const ProjectStartPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    phone: '',
    budget: '',
    company: '',
    metrics: '',
    projectType: '',
    timeline: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedService, setSelectedService] = useState('');

  const services = [
    {
      id: 'branding',
      title: 'Brand Strategy & Identity',
      icon: faLightbulb,
      description: 'Complete brand development from concept to execution',
      color: 'from-purple-500 to-pink-500'
    },
    {
      id: 'web-development',
      title: 'Web & App Development',
      icon: faRocket,
      description: 'Custom websites and mobile applications',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'digital-marketing',
      title: 'Digital Marketing',
      icon: faChartLine,
      description: 'SEO, social media, and growth strategies',
      color: 'from-green-500 to-emerald-500'
    },
    {
      id: 'video-production',
      title: 'Video Production',
      icon: faUsers,
      description: 'Creative video content and campaigns',
      color: 'from-red-500 to-orange-500'
    }
  ];

  const testimonials = [
    {
      name: "Sarah M.",
      company: "Ethio Coffee Co.",
      text: "Ato Creatives transformed our brand identity and increased our online engagement by 300% in just 3 months!",
      rating: 5
    },
    {
      name: "Michael T.",
      company: "Tech Solutions Inc.",
      text: "Their web development team delivered beyond our expectations. Professional, creative, and incredibly efficient.",
      rating: 5
    },
    {
      name: "Liya A.",
      company: "Habesha Fashion",
      text: "The digital marketing campaign resulted in a 150% ROI. Truly exceptional work!",
      rating: 5
    }
  ];

  useEffect(() => {
    api.get('/contacts/CreateContact')
      .then(response => setData(response.data))
      .catch(error => console.log(error));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleServiceSelect = (serviceId) => {
    setSelectedService(serviceId);
    setFormData({ ...formData, projectType: serviceId });
    setCurrentStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await api.post('/contacts/CreateContact', formData);
      
      Swal.fire({
        title: 'Project Initiated! 🚀',
        text: 'Congratulations! Your project journey has begun. Our team will contact you within 24 hours to discuss next steps.',
        icon: 'success',
        confirmButtonText: 'Awesome!',
        confirmButtonColor: '#FD5F20',
        background: '#1a202c',
        color: 'white',
      });

      setFormData({ 
        name: '', 
        email: '', 
        phone: '', 
        budget: '', 
        company: '', 
        metrics: '', 
        message: '',
        projectType: '',
        timeline: ''
      });
      setCurrentStep(1);
      setSelectedService('');
    } catch (error) {
      Swal.fire({
        title: 'Oops!',
        text: error.response?.data?.error || 'Something went wrong. Please try again.',
        icon: 'error',
        confirmButtonText: 'Try again',
        confirmButtonColor: '#FD5F20',
        background: '#1a202c',
        color: 'white',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <FontAwesomeIcon 
        key={i} 
        icon={faStar} 
        className={i < rating ? "text-yellow-400" : "text-gray-600"} 
        size="sm"
      />
    ));
  };

  return (
    <>
      <HeadComponent/>
      <div className="min-h-screen bg-black">
        {/* Navigation Progress */}
        <div className="fixed top-0 left-0 right-0 z-40 bg-gray-800/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <motion.div 
                className="flex items-center space-x-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="w-8 h-8 bg-gradient-to-r from-[#FD5F20] to-[#FF8C00] rounded-lg flex items-center justify-center">
                  <FontAwesomeIcon icon={faRocket} className="text-white text-sm" />
                </div>
                <span className="text-white font-bold text-xl">Start Your Project</span>
              </motion.div>
              
              <div className="flex items-center space-x-4">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                      currentStep >= step 
                        ? 'bg-[#FD5F20] text-white' 
                        : 'bg-gray-600 text-gray-300'
                    }`}>
                      {step}
                    </div>
                    {step < 3 && (
                      <div className={`w-12 h-1 mx-2 ${
                        currentStep > step ? 'bg-[#FD5F20]' : 'bg-gray-600'
                      }`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Hero Section */}
            <motion.div 
              className="text-center mb-16 pt-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6">
                Let's Create Something{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FD5F20] to-[#FF8C00]">
                  Amazing
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                Transform your vision into reality with our expert team. From concept to launch, 
                we're here to make your project a stunning success.
              </p>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-12 items-start">
              
              {/* Left Content - Dynamic Steps */}
              <div className="space-y-8">
                {currentStep === 1 && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-6"
                  >
                    <h2 className="text-3xl font-bold text-white mb-6">Choose Your Service</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      {services.map((service) => (
                        <motion.div
                          key={service.id}
                          className={`p-6 rounded-xl cursor-pointer transition-all duration-300 ${
                            selectedService === service.id
                              ? 'bg-gradient-to-r ' + service.color + ' scale-105'
                              : 'bg-gray-800 hover:bg-gray-700'
                          }`}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleServiceSelect(service.id)}
                        >
                          <div className="flex items-center space-x-4">
                            <div className={`p-3 rounded-lg ${
                              selectedService === service.id ? 'bg-white/20' : 'bg-gray-700'
                            }`}>
                              <FontAwesomeIcon 
                                icon={service.icon} 
                                className={`text-2xl ${
                                  selectedService === service.id ? 'text-white' : 'text-[#FD5F20]'
                                }`}
                              />
                            </div>
                            <div>
                              <h3 className={`text-lg font-semibold ${
                                selectedService === service.id ? 'text-white' : 'text-white'
                              }`}>
                                {service.title}
                              </h3>
                              <p className={`text-sm ${
                                selectedService === service.id ? 'text-white/90' : 'text-gray-400'
                              }`}>
                                {service.description}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {currentStep === 2 && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="space-y-6"
                  >
                    <h2 className="text-3xl font-bold text-white mb-6">Project Details</h2>
                    
                    <motion.div 
                      className="bg-gray-800 rounded-xl p-6 border-l-4 border-[#FD5F20]"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}
                    >
                      <h3 className="text-xl font-semibold text-white mb-4">Why Choose Us?</h3>
                      <div className="space-y-3">
                        {[
                          "Expert team with 5+ years experience",
                          "100+ successful projects delivered",
                          "24/7 project support and communication",
                          "Agile development methodology",
                          "Competitive pricing with no hidden costs"
                        ].map((feature, index) => (
                          <div key={index} className="flex items-center space-x-3">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-400" />
                            <span className="text-gray-300">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </motion.div>

                    {/* Testimonials */}
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-white">What Our Clients Say</h4>
                      {testimonials.map((testimonial, index) => (
                        <motion.div
                          key={index}
                          className="bg-gray-800 rounded-lg p-4"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <div className="flex items-center space-x-2 mb-2">
                            {renderStars(testimonial.rating)}
                          </div>
                          <p className="text-gray-300 text-sm mb-2">"{testimonial.text}"</p>
                          <div className="text-right">
                            <p className="text-white font-semibold">{testimonial.name}</p>
                            <p className="text-[#FD5F20] text-sm">{testimonial.company}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Right Form - Always Visible */}
              <motion.div 
                className="sticky top-24"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700 shadow-2xl">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-gradient-to-r from-[#FD5F20] to-[#FF8C00] rounded-lg flex items-center justify-center">
                      <FontAwesomeIcon icon={faPaperPlane} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">Start Your Project</h2>
                      <p className="text-gray-400">Fill in the details below</p>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <input
                          type="text"
                          name="name"
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent transition-all"
                          placeholder="Your Name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      
                      <div>
                        <input
                          type="tel"
                          name="phone"
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent transition-all"
                          placeholder="Phone Number"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      
                      <div>
                        <input
                          type="email"
                          name="email"
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent transition-all"
                          placeholder="Email Address"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      
                      <div>
                        <input
                          type="text"
                          name="company"
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent transition-all"
                          placeholder="Company Name"
                          value={formData.company}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="relative">
                        <select
                          name="budget"
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white appearance-none pr-10 focus:outline-none focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
                          value={formData.budget}
                          onChange={handleChange}
                          required
                        >
                          <option value="" className="text-gray-400">Project Budget</option>
                          <option value="<5k" className="text-white">&lt; 5,000 ETB</option>
                          <option value="5k-10k" className="text-white">5,000 - 10,000 ETB</option>
                          <option value="10k-20k" className="text-white">10,000 - 20,000 ETB</option>
                          <option value="20k-30k" className="text-white">20,000 - 30,000 ETB</option>
                          <option value="30k-40k" className="text-white">30,000 - 40,000 ETB</option>
                          <option value="50k+" className="text-white">50,000+ ETB</option>
                        </select>
                        <FontAwesomeIcon 
                          icon={faChevronDown} 
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#FD5F20] pointer-events-none"
                        />
                      </div>

                      <div className="relative">
                        <select
                          name="timeline"
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white appearance-none pr-10 focus:outline-none focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent"
                          value={formData.timeline}
                          onChange={handleChange}
                          required
                        >
                          <option value="" className="text-gray-400">Project Timeline</option>
                          <option value="urgent" className="text-white">Urgent (1-2 weeks)</option>
                          <option value="standard" className="text-white">Standard (3-4 weeks)</option>
                          <option value="extended" className="text-white">Extended (1-2 months)</option>
                          <option value="long-term" className="text-white">Long-term (3+ months)</option>
                        </select>
                        <FontAwesomeIcon 
                          icon={faChevronDown} 
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#FD5F20] pointer-events-none"
                        />
                      </div>
                    </div>

                    <div>
                      <textarea
                        name="message"
                        rows="5"
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FD5F20] focus:border-transparent transition-all resize-none"
                        placeholder="Tell us about your project vision, goals, and any specific requirements..."
                        value={formData.message}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <motion.button
                      type="submit"
                      className="w-full bg-gradient-to-r from-[#FD5F20] to-[#FF8C00] text-white font-bold py-4 px-6 rounded-lg hover:shadow-lg transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Request Your Project...</span>
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faRocket} />
                          <span>Request Project</span>
                        </>
                      )}
                    </motion.button>

                    <div className="text-center">
                      <p className="text-gray-400 text-sm">
                        <FontAwesomeIcon icon={faShieldAlt} className="mr-2" />
                        Your information is secure and confidential
                      </p>
                    </div>
                  </form>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectStartPage;